import { useEffect, useRef, useState } from "react";
import { App, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { getAppSecurity, type AppSecurity } from "@/api/security";
import { PageTabs } from "@/components/Tabs";
import { useCurrentApp } from "@/hooks/useCurrentApp";
import { Banner, SecuritySection, type SecurityKind } from "./component";

const EMPTY_SECURITY: AppSecurity = {
  redirect_allowlist: [],
  ip_allowlist: [],
  h5_trusted_domains: [],
};

const FIELD = {
  redirect: "redirect_allowlist",
  ip: "ip_allowlist",
  h5: "h5_trusted_domains",
} as const satisfies Record<SecurityKind, keyof AppSecurity>;

function Security() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const app = useCurrentApp();
  const [tab, setTab] = useState<SecurityKind>("redirect");
  const [loading, setLoading] = useState(true);
  const [security, setSecurity] = useState<AppSecurity>(EMPTY_SECURITY);
  const messageRef = useRef(message);
  const tRef = useRef(t);
  messageRef.current = message;
  tRef.current = t;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSecurity(EMPTY_SECURITY);
    void getAppSecurity(app.id)
      .then((settings) => {
        if (!cancelled) setSecurity(settings);
      })
      .catch(() => {
        if (!cancelled) {
          messageRef.current.error(tRef.current("security.loadFailed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [app.id]);

  const field = FIELD[tab];

  return (
    <div className="flex flex-col gap-5 m-5">
      <Banner />
      <PageTabs
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: "redirect", label: t("security.tabRedirect") },
          { key: "ip", label: t("security.tabIp") },
          { key: "h5", label: t("security.tabH5") },
        ]}
      />
      <Spin spinning={loading}>
        <SecuritySection
          kind={tab}
          appId={app.id}
          list={security[field]}
          onListChange={(updater) =>
            setSecurity((current) => ({
              ...current,
              [field]: updater(current[field]),
            }))
          }
        />
      </Spin>
    </div>
  );
}

export default Security;
